import React, { useEffect, useMemo, useState } from "react"
import { Container, Row, Col, Modal, Label, Input } from "reactstrap"
import Select from "react-select"
import axios from "axios"
import Swal from "sweetalert2"
import moment from "moment"
import { useSelector } from "react-redux"
import Breadcrumbs from "components/Common/Breadcrumb"
import Loader from "pages/Separate/Loader"
import secureLocalStorage from "react-secure-storage"
import configure from "configure"
import { fetchNodeData, FetchNodePostData } from "utils/common-fetchData"
import { colourStyles } from "utils/common-options"
import "./bankStatementConsole.css"
import StatementExportModal from "./StatementExportModal"
import {
    AccountInfoLine,
    AccountOptionLabel,
    buildSuggestedGroups,
    formatMatchOption,
    toAccountCounts,
    toExpenseOption,
} from "./statementMatchHelpers"
// import { STATEMENT_MODULES, useStatementPermissions } from "./statementEntryActions" — role access disabled for now
import { useStatementEntryActions } from "./statementEntryActions"

// Credit-card statement reconcile — cloned from BankStatementConsole with a
// reduced match set:
//   DEBIT  (charge/spend on the card) -> Expense only (payment_via = 2)
//   CREDIT (payment/refund into the card) -> Bill Payment only (repayment
//     made from a bank account, recorded as a transfer/acknowledgement)
// Removed vs bank: Invoice Payment (receipts land in bank, never in CC),
// Overdraft Withdraw/Deposit (OD table + bal_limit/used_limit is bank-only),
// Shareholder Investment/Return (shareholder money hits bank, not CC).
const CREDIT_MATCH_TYPES = [
    { value: "bill_payment", label: "Bill Payment (Paid from Bank)" },
]
const DEBIT_MATCH_TYPES = [
    { value: "expense", label: "Expense" },
]

const MATCH_TYPE_LABELS = Object.fromEntries(
    [...CREDIT_MATCH_TYPES, ...DEBIT_MATCH_TYPES].map(t => [t.value, t.label])
)

// Cards are finance-modes flagged payment_via === "2" (vs "1" = bank).
// Keep BOTH numeric id (payment_method_id when creating expense records)
// AND lower-cased payment_mode label as card_name for fetch/upload.

const CreditCardStatementConsole = () => {
    document.title = "Credit Card Reconsile | Zithas Crm"
    const token = secureLocalStorage.getItem("auth_token")
    const [loading, setLoading] = useState(false)
    // Roles & Responsibilities: add = Import / Add Entry, edit = Edit,
    // delete = Delete. View is enforced by the route itself.
    // const { canAdd, canEdit, canDelete } = useStatementPermissions(STATEMENT_MODULES.card) — role access disabled for now
    const canAdd = true
    const canEdit = true
    const canDelete = true

    const [cardOptions, setCardOptions] = useState([])
    const [selectedCard, setSelectedCard] = useState(null)
    const [exportModal, setExportModal] = useState(false)
    // Bank accounts (payment_via "1") — only used as "Paid From" inside the
    // Bill Payment block, not as the statement owner.
    const [bankOptions, setBankOptions] = useState([])
    const [entries, setEntries] = useState([])
    const [file, setFile] = useState(null)
    const [fileKey, setFileKey] = useState(0)

    const [summary, setSummary] = useState({ total: 0, matched: 0, unmatched: 0 })
    const [cardCounts, setCardCounts] = useState({})

    const [activeTab, setActiveTab] = useState("unmatched")
    const [search, setSearch] = useState("")
    const [debouncedSearch, setDebouncedSearch] = useState("")
    const [page, setPage] = useState(1)
    const [pageSize, setPageSize] = useState(10)
    const [total, setTotal] = useState(0)
    const [tabCounts, setTabCounts] = useState({ matched: 0, unmatched: 0 })

    const [matchModal, setMatchModal] = useState(false)
    const [activeEntry, setActiveEntry] = useState(null)
    const [matchType, setMatchType] = useState(null)

    // expense fields
    const [expenseAction, setExpenseAction] = useState("new")
    const [categoryOptions, setCategoryOptions] = useState([])
    const [selectedCategory, setSelectedCategory] = useState(null)
    const [expenseTitle, setExpenseTitle] = useState("")
    const [expenseLocation, setExpenseLocation] = useState(null)
    const [expenseOptions, setExpenseOptions] = useState([])
    const [selectedExpense, setSelectedExpense] = useState(null)

    // bill-payment fields — which bank account repaid this card
    const [paidFromBank, setPaidFromBank] = useState(null)

    // shared fields
    const [amount, setAmount] = useState("")
    const [matchDate, setMatchDate] = useState("")
    const [notes, setNotes] = useState("")

    // Map-to-existing-entry — same idea as bank console: "create" books a
    // brand new expense row, "map" links this statement row directly to the
    // EXPENSE itself. Expense stays manually choosable (due only picks a
    // sensible default). Bill Payment is always create (a transfer record).
    const [matchMode, setMatchMode] = useState("create")

    let getLocations = useSelector((state) => state?.location?.locations)
    getLocations = useMemo(
        () => getLocations?.filter((l) => configure.location.includes(l.value)) || [],
        [getLocations]
    )

    useEffect(() => {
        const t = setTimeout(() => setDebouncedSearch(search.trim()), 400)
        return () => clearTimeout(t)
    }, [search])

    const fetchEntries = async cardName => {
        if (!cardName) return
        const params = new URLSearchParams({
            page: String(page),
            limit: String(pageSize),
            status: activeTab,
        })
        if (selectedCard?.value) params.set("card_id", String(selectedCard.value))
        if (debouncedSearch) params.set("search", debouncedSearch)
        const res = await fetchNodeData(setLoading, `creditCardStatement/${cardName}?${params.toString()}`, token)
        if (Array.isArray(res?.data)) {
            setEntries(res.data)
            setTotal(res?.pagination?.total || 0)
            setTabCounts({ matched: res?.counts?.matched || 0, unmatched: res?.counts?.unmatched || 0 })
        }
    }

    const fetchSummary = async () => {
        const res = await fetchNodeData(() => { }, "creditCardStatement/summary/counts", token)
        if (res?.success) {
            setSummary({ total: res.total, matched: res.matched, unmatched: res.unmatched })
        }
    }

    const fetchCardCounts = async () => {
        const res = await fetchNodeData(() => { }, "creditCardStatement/summary/by-card", token)
        if (res?.success && Array.isArray(res?.data)) {
            const map = {}
            res.data.forEach(r => {
                if (r?.card_name) map[r.card_name] = toAccountCounts(r)
                else if (r?.bank_name) map[r.bank_name] = toAccountCounts(r)
            })
            setCardCounts(map)
        }
    }

    const formatCardOptionLabel = (option, { context }) => (
        <AccountOptionLabel label={option?.label} counts={cardCounts?.[option?.card_name]} context={context} />
    )

    useEffect(() => {
        fetchSummary()
        fetchCardCounts()
        fetchNodeData(setLoading, "financeCategory", token).then(res => {
            if (res?.success) {
                setCategoryOptions(res?.data?.map(c => ({ value: c.id, label: c.name })) || [])
            }
        })
        fetchNodeData(() => { }, "financeModes", token).then(res => {
            if (res?.success) {
                const cards = (res?.data || []).filter(m => String(m.payment_via) === "2")
                setCardOptions(
                    cards.map(m => ({
                        value: m.id,
                        label: m.payment_mode,
                        card_name: (m.payment_mode || "").trim().toLowerCase(),
                        payment_via: String(m.payment_via),
                    }))
                )
                const banks = (res?.data || []).filter(m => String(m.payment_via) === "1")
                setBankOptions(
                    banks.map(m => ({
                        value: m.id,
                        label: m.payment_mode,
                    }))
                )
            }
        })
    }, [])

    useEffect(() => {
        fetchEntries(selectedCard?.card_name)
    }, [selectedCard, page, pageSize, activeTab, debouncedSearch])

    const onUpload = async () => {
        if (!selectedCard) {
            Swal.fire({ icon: "warning", text: "Please select a credit card first." })
            return
        }
        if (!file) {
            Swal.fire({ icon: "warning", text: "Please choose an Excel file to upload." })
            return
        }
        setLoading(true)
        try {
            const formData = new FormData()
            formData.append("card_name", selectedCard.card_name)
            formData.append("card_id", selectedCard.value)
            formData.append("file", file)
            const res = await axios.post(`${configure.nodeApi}/creditCardStatement/upload`, formData, {
                headers: { Authorization: `Bearer ${token}` },
            })
            if (Array.isArray(res?.data?.data)) {
                Swal.fire({ icon: "success", text: res?.data?.message || "Upload complete" })
                setFile(null)
                setFileKey(k => k + 1)
                fetchEntries(selectedCard?.card_name)
                fetchSummary()
                fetchCardCounts()
            } else {
                Swal.fire({ icon: "error", text: res?.data?.message || "Upload failed" })
            }
        } catch (err) {
            Swal.fire({ icon: "error", text: err?.response?.data?.message || err?.message })
        } finally {
            setLoading(false)
        }
    }

    const openMatchModal = entry => {
        setActiveEntry(entry)
        setMatchType(null)
        setExpenseAction("new")
        setSelectedCategory(null)
        setExpenseTitle("")
        setExpenseLocation(null)
        setSelectedExpense(null)
        setExpenseOptions([])
        setPaidFromBank(null)
        setNotes("")
        setMatchMode("create")
        setAmount(Number(entry?.credit) > 0 ? entry?.credit : entry?.debit)
        setMatchDate(entry?.statement_date ? moment(entry.statement_date).format("YYYY-MM-DD") : moment().format("YYYY-MM-DD"))
        // Each side has exactly one match type, so pick it directly instead
        // of asking — debit rows open straight into New / Existing Expense.
        onSelectMatchType(Number(entry?.credit) > 0 ? CREDIT_MATCH_TYPES[0] : DEBIT_MATCH_TYPES[0])
        setMatchModal(true)
    }

    const isCreditEntry = Number(activeEntry?.credit) > 0
    const entryAmount = activeEntry ? (isCreditEntry ? Number(activeEntry.credit) : Number(activeEntry.debit)) || 0 : 0

    const onSelectMatchType = type => {
        setMatchType(type)
        setMatchMode("create")
        if (type?.value === "expense") {
            // Location 1 = the same set the old budgetExpense/2/1 call listed,
            // but complete — that endpoint only returns its first page of 50.
            fetchNodeData(setLoading, "creditCardStatement/expense/options?location=1", token).then(res => {
                if (res?.success) setExpenseOptions((res?.data || []).map(toExpenseOption))
            })
        }
    }

    // Suggested-first expense list: closest in amount to this statement row
    // (or named in its description) on top, every registered expense after.
    const expenseSelectOptions = useMemo(
        () => buildSuggestedGroups(expenseOptions, expenseOptions, entryAmount, activeEntry?.description, { tolerance: 0.05, allLabel: "All registered expenses" }),
        [expenseOptions, entryAmount, activeEntry]
    )

    // Expense — "Add New Expense" always creates. "Pay Registered Expense"
    // only sets a DEFAULT from due when a different expense is picked; the
    // manual Add/Map toggle can still override afterwards.
    useEffect(() => {
        if (!matchModal || matchType?.value !== "expense") return
        if (expenseAction !== "existing" || !selectedExpense) {
            setMatchMode("create")
            return
        }
        setMatchMode(Math.abs(Number(selectedExpense.due) || 0) < 0.01 ? "map" : "create")
    }, [matchModal, matchType, expenseAction, selectedExpense])

    const postMatch = async body => {
        const res = await FetchNodePostData(setLoading, "creditCardStatement/match", token, body)
        const data = res?.data
        if (data?.success) {
            Swal.fire({ icon: "success", text: "Entry matched successfully." })
            setMatchModal(false)
            fetchEntries(selectedCard?.card_name)
            fetchSummary()
            fetchCardCounts()
            return
        }
        Swal.fire({ icon: "error", text: data?.message || "Could not match this entry." })
    }

    const onMapToTarget = async () => {
        if (!matchType || matchType.value !== "expense") return
        if (!selectedExpense) return
        const confirmResult = await Swal.fire({
            title: "Map this statement row?",
            html: `Link this statement row to <b>${selectedExpense.label}</b>?<br/>No new payment will be created — this only records the link.`,
            icon: "question",
            showCancelButton: true,
            confirmButtonText: "Yes, map it",
        })
        if (!confirmResult.isConfirmed) return
        await postMatch({
            entry_id: activeEntry.id,
            matched_type: matchType.value,
            mode: "map",
            notes: notes || null,
            expense_id: selectedExpense.value,
        })
    }

    const onSubmitMatch = async () => {
        if (!matchType) {
            Swal.fire({ icon: "warning", text: "Please select where this entry belongs." })
            return
        }
        if (!selectedCard) {
            Swal.fire({ icon: "warning", text: "Please select a card again — the card context was lost." })
            return
        }
        if (matchMode === "map") {
            Swal.fire({ icon: "info", text: "Click the Map button above to complete this match." })
            return
        }
        if (!(Number(amount) > 0)) {
            Swal.fire({ icon: "warning", text: "Please enter an amount greater than zero." })
            return
        }

        const body = {
            entry_id: activeEntry.id,
            matched_type: matchType.value,
            notes: notes || null,
        }

        if (matchType.value === "expense") {
            if (expenseAction === "new") {
                if (!selectedCategory || !expenseTitle) {
                    Swal.fire({ icon: "warning", text: "Please fill category and title." })
                    return
                }
                Object.assign(body, {
                    expense_action: "new",
                    category: selectedCategory.value,
                    title: expenseTitle,
                    payment_via: 2, // Credit Card — row came off a card statement
                    payment_method_id: selectedCard.value,
                    amount: Number(amount),
                    date: matchDate ? moment(matchDate).format("YYYY-MM-DD") : moment().format("YYYY-MM-DD"),
                    location: expenseLocation?.value || 2,
                })
            } else {
                if (!selectedExpense) {
                    Swal.fire({ icon: "warning", text: "Please select an expense." })
                    return
                }
                Object.assign(body, {
                    expense_action: "existing",
                    expense_id: selectedExpense.value,
                    payment_mode_id: selectedCard.value,
                    payment_method_id: selectedCard.value,
                    payment_via: 2,
                    amount: Number(amount),
                    date: matchDate ? moment(matchDate).format("YYYY-MM-DD") : moment().format("YYYY-MM-DD"),
                })
            }
        } else if (matchType.value === "bill_payment") {
            if (!paidFromBank) {
                Swal.fire({ icon: "warning", text: "Please select which bank account paid this bill." })
                return
            }
            Object.assign(body, {
                paid_from_bank_id: paidFromBank.value,
                amount: Number(amount),
                date: matchDate ? moment(matchDate).format("YYYY-MM-DD") : moment().format("YYYY-MM-DD"),
            })
        }

        await postMatch(body)
    }

    const onRematch = async entry => {
        const isMap = entry.link_mode === "map"
        const result = await Swal.fire({
            title: "Rematch this entry?",
            html: isMap
                ? `This will unlink this statement row from <b>${String(entry.matched_ref_table || "").replace(/_/g, " ")} (ID ${entry.matched_ref_id})</b>. Nothing was created for this match, so there is nothing to delete — the linked record itself is left untouched.`
                : `This will <b>reverse</b> the previous match — the <b>${String(entry.matched_ref_table || entry.matched_type || "").replace(/_/g, " ")}</b> record it created (ID ${entry.matched_ref_id}) will be deleted and dues restored — so you can reclassify it. If that record was changed since matching, you'll be asked to fix it on its own page first.`,
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Yes, unmatch it",
        })
        if (!result.isConfirmed) return

        const res = await FetchNodePostData(setLoading, "creditCardStatement/unmatch", token, { entry_id: entry.id })
        if (res?.data?.success) {
            await fetchEntries(selectedCard?.card_name)
            fetchSummary()
            fetchCardCounts()
            const lines = [...(res?.data?.reversed || [])]
            if (res?.data?.warning) lines.push(res.data.warning)
            const confirm = await Swal.fire({
                icon: res?.data?.warning ? "warning" : "success",
                title: "Entry unmatched",
                html: (lines.length ? lines.map(l => `<div>${l}</div>`).join("") + "<br/>" : "") + "Pick a new classification for this entry?",
                showCancelButton: true,
                confirmButtonText: "Reclassify",
                cancelButtonText: "Later",
            })
            if (confirm.isConfirmed) {
                openMatchModal({ ...entry, status: "unprocessed", matched_type: null, matched_ref_id: null, matched_ref_table: null })
            }
        } else {
            Swal.fire({ icon: "error", text: res?.data?.message || "Could not unmatch this entry." })
        }
    }

    const bankMappedCount = tabCounts.matched
    const bankUnmatchedCount = tabCounts.unmatched

    const pageEntries = entries
    const totalPages = Math.max(1, Math.ceil(total / pageSize))
    const currentPage = Math.min(page, totalPages)

    useEffect(() => {
        setPage(1)
    }, [debouncedSearch, selectedCard, pageSize, activeTab])

    const pageNumbers = useMemo(() => {
        const delta = 2
        const range = []
        const withDots = []
        for (let i = 1; i <= totalPages; i++) {
            if (i === 1 || i === totalPages || (i >= currentPage - delta && i <= currentPage + delta)) {
                range.push(i)
            }
        }
        let last = 0
        for (const i of range) {
            if (last && i - last > 1) withDots.push("...")
            withDots.push(i)
            last = i
        }
        return withDots
    }, [totalPages, currentPage])

    const { openAdd, openEdit, onDelete, entryModal } = useStatementEntryActions({
        apiBase: "creditCardStatement",
        account: selectedCard,
        nameKey: "card_name",
        idKey: "card_id",
        accountWord: "card",
        token,
        setLoading,
        onChanged: () => {
            fetchEntries(selectedCard?.card_name)
            fetchSummary()
            fetchCardCounts()
        },
    })

    return (
        <React.Fragment>
            {loading && <Loader />}
            <div className="page-content">
                <Container fluid className="bsc-page">
                    <Breadcrumbs title={"Reports"} breadcrumbItem={"Credit Card Reconsile"} />

                    <div className="bsc-stats">
                        <div className="bsc-stat-card">
                            <div className="bsc-stat-icon total">
                                <i className="fas fa-list-ul"></i>
                            </div>
                            <div>
                                <div className="bsc-stat-label">Total Entries (all cards)</div>
                                <div className="bsc-stat-value">{summary.total}</div>
                            </div>
                        </div>
                        <div className="bsc-stat-card">
                            <div className="bsc-stat-icon mapped">
                                <i className="fas fa-check-circle"></i>
                            </div>
                            <div>
                                <div className="bsc-stat-label">Mapped Entries</div>
                                <div className="bsc-stat-value">{summary.matched}</div>
                            </div>
                        </div>
                        <div className="bsc-stat-card">
                            <div className="bsc-stat-icon remaining">
                                <i className="fas fa-hourglass-half"></i>
                            </div>
                            <div>
                                <div className="bsc-stat-label">Remaining Entries</div>
                                <div className="bsc-stat-value">{summary.unmatched}</div>
                            </div>
                        </div>
                    </div>

                    <div className="bsc-card">
                        {/* One line: account + file + Import grouped on the left as the
                            upload section, Export on its own at the right edge. */}
                        <div className="bsc-action-row">
                            <div className="bsc-upload-group">
                                <div className="bsc-upload-field">
                                    <Label>Select Credit Card</Label>
                                    <Select
                                        placeholder="Select Credit Card"
                                        options={cardOptions}
                                        value={selectedCard}
                                        onChange={setSelectedCard}
                                        formatOptionLabel={formatCardOptionLabel}
                                        styles={colourStyles}
                                    />
                                </div>
                                {canAdd && (
                                    <>
                                        <div className="bsc-upload-field">
                                            <Label>Upload Credit Card Statement (Excel)</Label>
                                            <Input key={fileKey} type="file" accept=".xlsx,.xls,.csv" onChange={e => setFile(e.target.files[0])} data-testid="statement-file" />
                                        </div>
                                        <button className="bsc-import-btn" onClick={onUpload} data-testid="statement-import">
                                            <i className="fas fa-upload me-2"></i>
                                            Import
                                        </button>
                                    </>
                                )}
                            </div>
                            <div className="bsc-right-actions">
                                {canAdd && (
                                    <button className="bsc-import-btn bsc-export-btn" onClick={openAdd} title="Add a single statement entry" data-testid="statement-add">
                                        <i className="fas fa-plus me-2"></i>
                                        Add Entry
                                    </button>
                                )}
                                <button
                                    className="bsc-import-btn bsc-export-btn"
                                    onClick={() => setExportModal(true)}
                                    title="Export entries by card, date range and status"
                                    data-testid="statement-export"
                                >
                                    <i className="fas fa-file-excel me-2"></i>
                                    Export
                                </button>
                            </div>
                        </div>
                        {selectedCard && <AccountInfoLine counts={cardCounts?.[selectedCard.card_name]} />}
                    </div>

                    <div className="bsc-card">
                        <div className="bsc-tabs">
                            <button
                                className={`bsc-tab ${activeTab === "unmatched" ? "active" : ""}`}
                                onClick={() => setActiveTab("unmatched")}
                            >
                                Unmatched {selectedCard ? `(${bankUnmatchedCount})` : ""}
                            </button>
                            <button
                                className={`bsc-tab ${activeTab === "matched" ? "active" : ""}`}
                                onClick={() => setActiveTab("matched")}
                            >
                                Matched {selectedCard ? `(${bankMappedCount})` : ""}
                            </button>
                        </div>

                        <div className="bsc-toolbar">
                            <div className="bsc-search">
                                <i className="fas fa-search"></i>
                                <input
                                    type="text"
                                    placeholder="Search by description or reference..."
                                    value={search}
                                    onChange={e => setSearch(e.target.value)}
                                />
                            </div>
                            <div className="bsc-page-size">
                                <select value={pageSize} onChange={e => setPageSize(Number(e.target.value))}>
                                    {[10, 25, 50, 100].map(n => (
                                        <option key={n} value={n}>Show {n}</option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        <div className="bsc-table-wrap">
                            <table className="bsc-table">
                                <thead>
                                    <tr>
                                        <th>Date</th>
                                        <th>Reference</th>
                                        <th>Description</th>
                                        <th className="text-end">Debit</th>
                                        <th className="text-end">Credit</th>
                                        <th className="text-center">Status</th>
                                        <th className="text-center">Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {pageEntries.map(entry => (
                                        <tr key={entry.id} data-testid="statement-row" data-entry-id={entry.id}>
                                            <td>{entry.statement_date ? moment(entry.statement_date).format("DD-MM-YYYY") : ""}</td>
                                            <td>{entry.reference_no}</td>
                                            <td className="bsc-desc" title={entry.description}>{entry.description}</td>
                                            <td className="text-end">{Number(entry.debit) ? `₹ ${Number(entry.debit).toFixed(2)}` : ""}</td>
                                            <td className="text-end">{Number(entry.credit) ? `₹ ${Number(entry.credit).toFixed(2)}` : ""}</td>
                                            <td className="text-center">
                                                {entry.status === "matched" ? (
                                                    <span
                                                        className="bsc-pill matched"
                                                        title={[notes].filter(Boolean).join(" — ") || undefined}
                                                    >
                                                        <i className="fas fa-check"></i>
                                                        {MATCH_TYPE_LABELS[entry.matched_type] || entry.matched_type}
                                                    </span>
                                                ) : (
                                                    <span className="bsc-pill unprocessed">
                                                        <i className="fas fa-clock"></i>
                                                        Unprocessed
                                                    </span>
                                                )}
                                            </td>
                                            <td className="text-center">
                                                {entry.status !== "matched" ? (
                                                    <div className="bsc-row-actions">
                                                        <button className="bsc-match-btn" title="Match" onClick={() => openMatchModal(entry)} data-testid="row-match">
                                                            <i className="fas fa-handshake"></i>
                                                        </button>
                                                        {/* Edit/Delete only while unmatched — a matched row
                                                            has booked records from its date and amount. */}
                                                        {canEdit && (
                                                            <button className="bsc-match-btn edit" title="Edit" onClick={() => openEdit(entry)} data-testid="row-edit">
                                                                <i className="fas fa-pen"></i>
                                                            </button>
                                                        )}
                                                        {canDelete && (
                                                            <button className="bsc-match-btn delete" title="Delete" onClick={() => onDelete(entry)} data-testid="row-delete">
                                                                <i className="fas fa-trash-alt"></i>
                                                            </button>
                                                        )}
                                                    </div>
                                                ) : (
                                                    <button className="bsc-match-btn rematch" title="Rematch" onClick={() => onRematch(entry)}>
                                                        <i className="fas fa-rotate"></i>
                                                    </button>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                    {pageEntries.length === 0 && (
                                        <tr>
                                            <td colSpan={7} className="text-center">
                                                {!selectedCard
                                                    ? "Select a credit card to view its statement."
                                                    : total > 0
                                                        ? ""
                                                        : debouncedSearch
                                                            ? "No entries match your search."
                                                            : activeTab === "matched"
                                                                ? "No matched entries yet."
                                                                : "No unmatched entries for this card yet."}
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>

                        {total > 0 && (
                            <div className="bsc-pagination">
                                <div className="bsc-pagination-info">
                                    Showing {(currentPage - 1) * pageSize + 1} to{" "}
                                    {Math.min(currentPage * pageSize, total)} of {total} entries
                                </div>
                                <div className="bsc-pagination-controls">
                                    <button
                                        className="bsc-page-btn"
                                        disabled={currentPage === 1}
                                        onClick={() => setPage(p => Math.max(1, p - 1))}
                                    >
                                        <i className="fas fa-chevron-left"></i>
                                    </button>
                                    {pageNumbers.map((n, i) =>
                                        n === "..." ? (
                                            <span key={`dots-${i}`} className="bsc-page-dots">…</span>
                                        ) : (
                                            <button
                                                key={n}
                                                className={`bsc-page-btn ${n === currentPage ? "active" : ""}`}
                                                onClick={() => setPage(n)}
                                            >
                                                {n}
                                            </button>
                                        )
                                    )}
                                    <button
                                        className="bsc-page-btn"
                                        disabled={currentPage === totalPages}
                                        onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                                    >
                                        <i className="fas fa-chevron-right"></i>
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </Container>
            </div>

            <Modal isOpen={matchModal} size="lg" centered toggle={() => setMatchModal(false)}>
                <div className="modal-header">
                    <h5 className="modal-title mt-0">Match Entry</h5>
                    <button type="button" onClick={() => setMatchModal(false)} className="btn-modal-close">
                        <span className="btn-modal-span">&times;</span>
                    </button>
                </div>
                <div className="modal-body">
                    <Row className="mb-3">
                        <Col md={6}>
                            <Label>Description</Label>
                            <div className="text-muted">{activeEntry?.description}</div>
                            {selectedCard && <small className="text-muted">Card: {selectedCard.label} (id {selectedCard.value}, via 2)</small>}
                        </Col>
                        <Col md={3}>
                            <Label>Amount (₹)</Label>
                            <Input type="number" value={amount} onChange={e => setAmount(e.target.value)} disabled={matchMode === "map"} />
                            {matchMode === "map" ? (
                                <small className="text-muted">Not used — the statement&apos;s own amount (₹{Number(amount || 0).toFixed(2)}) is recorded as-is.</small>
                            ) : (
                                <small className="text-muted">Charge/spend on this card, in INR.</small>
                            )}
                        </Col>
                        <Col md={3}>
                            <Label>Date</Label>
                            <Input type="date" value={matchDate} onChange={e => setMatchDate(e.target.value)} disabled={matchMode === "map"} />
                        </Col>
                    </Row>

                    {matchType?.value === "expense" && (
                        <>
                            <Row className="mb-3">
                                <Col md={12}>
                                    <div className="d-flex gap-3">
                                        <div>
                                            <input
                                                type="radio"
                                                id="exp-new"
                                                checked={expenseAction === "new"}
                                                onChange={() => setExpenseAction("new")}
                                            />
                                            <label htmlFor="exp-new" className="ms-1">Add New Expense</label>
                                        </div>
                                        <div>
                                            <input
                                                type="radio"
                                                id="exp-existing"
                                                checked={expenseAction === "existing"}
                                                onChange={() => setExpenseAction("existing")}
                                            />
                                            <label htmlFor="exp-existing" className="ms-1">Pay Registered Expense</label>
                                        </div>
                                    </div>
                                </Col>
                            </Row>
                            {expenseAction === "new" ? (
                                <Row className="mb-3">
                                    <Col md={4}>
                                        <Label>Category</Label>
                                        <Select options={categoryOptions} value={selectedCategory} onChange={setSelectedCategory} styles={colourStyles} />
                                    </Col>
                                    <Col md={5}>
                                        <Label>Title</Label>
                                        <Input type="text" value={expenseTitle} onChange={e => setExpenseTitle(e.target.value)} />
                                    </Col>
                                    <Col md={3}>
                                        <Label>Location</Label>
                                        <Select options={getLocations} value={expenseLocation} onChange={setExpenseLocation} styles={colourStyles} />
                                    </Col>
                                </Row>
                            ) : (
                                <>
                                    <Row className="mb-3">
                                        <Col md={12}>
                                            <Label>Expense</Label>
                                            <Select
                                                options={expenseSelectOptions}
                                                value={selectedExpense}
                                                onChange={setSelectedExpense}
                                                formatOptionLabel={formatMatchOption}
                                                styles={colourStyles}
                                            />
                                        </Col>
                                    </Row>

                                    {selectedExpense && (
                                        <>
                                            <Row className="mb-3">
                                                <Col md={12}>
                                                    <div className="bsc-tabs" style={{ marginBottom: 0 }}>
                                                        <button
                                                            type="button"
                                                            className={`bsc-tab ${matchMode === "create" ? "active" : ""}`}
                                                            onClick={() => setMatchMode("create")}
                                                        >
                                                            Add New Payment
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className={`bsc-tab ${matchMode === "map" ? "active" : ""}`}
                                                            onClick={() => setMatchMode("map")}
                                                        >
                                                            Map to Existing Payment
                                                        </button>
                                                    </div>
                                                    <small className="text-muted d-block mt-1">
                                                        {matchMode === "map"
                                                            ? "This statement row will be LINKED to this expense — nothing new gets created."
                                                            : Math.abs(Number(selectedExpense.due) || 0) < 0.01
                                                                ? "Heads up — this expense already shows ₹0 due. Adding a new payment records an extra amount against it. If this statement row is the original payment, use Map instead."
                                                                : "This will record a new payment against this expense."}
                                                    </small>
                                                </Col>
                                            </Row>

                                            {matchMode === "map" && (
                                                <Row className="mb-3">
                                                    <Col md={12}>
                                                        <button type="button" className="btn btn-purple btn-label" onClick={onMapToTarget}>
                                                            <i className="fas fa-link me-2 icon-size label-icon"></i>
                                                            Map to This Expense
                                                        </button>
                                                    </Col>
                                                </Row>
                                            )}
                                        </>
                                    )}
                                </>
                            )}
                        </>
                    )}

                    {matchType?.value === "bill_payment" && (
                        <Row className="mb-3">
                            <Col md={12}>
                                <Label>Paid From Bank</Label>
                                <Select options={bankOptions} value={paidFromBank} onChange={setPaidFromBank} styles={colourStyles} placeholder="Select bank account this card bill was paid from" />
                                <small className="text-muted d-block mt-1">
                                    This will record a bill payment of ₹{Number(entryAmount || 0).toFixed(2)} against this card, paid from the selected bank.
                                </small>
                            </Col>
                        </Row>
                    )}

                    {matchType && (
                        <Row className="mb-3">
                            <Col md={12}>
                                <Label>Notes</Label>
                                <Input
                                    type="textarea"
                                    rows={2}
                                    placeholder="Optional — kept on this entry for reference"
                                    value={notes}
                                    onChange={e => setNotes(e.target.value)}
                                />
                            </Col>
                        </Row>
                    )}
                </div>
                <div className="modal-footer">
                    <button className="btn btn-red btn-label me-1" onClick={() => setMatchModal(false)}>
                        <i className="dripicons-cross me-2 icon-center label-icon"></i>
                        Cancel
                    </button>
                    {matchMode === "map" ? (
                        <span className="text-muted" style={{ fontSize: 13, alignSelf: "center" }}>
                            Use the Map button above to complete this match.
                        </span>
                    ) : (
                        <button className="btn btn-purple btn-label" onClick={onSubmitMatch}>
                            <i className="fas fa-check me-2 icon-size label-icon"></i>
                            Confirm Match
                        </button>
                    )}
                </div>
            </Modal>

            {entryModal}

            <StatementExportModal
                isOpen={exportModal}
                toggle={() => setExportModal(false)}
                token={token}
                setLoading={setLoading}
                endpoint="creditCardStatement/export/entries"
                idsParam="card_ids"
                accountLabel="Card"
                sourceKey="card_name"
                accountOptions={cardOptions}
                defaultAccount={selectedCard}
                typeLabels={MATCH_TYPE_LABELS}
                filePrefix="Credit_Card_Statement"
            />
        </React.Fragment>
    )
}

export default CreditCardStatementConsole
